/* eslint-disable max-classes-per-file */
import frameTimer from '../service/frameTimer';

const {
    setInterval, clearInterval, setTimeout, clearTimeout, Date
} = globalThis;

class TimerStash
{
    #persistent = false;
    #items = {};

    get keys()
    {
        return Object.keys(this.#items);
    }

    set persistent(persistent)
    {
        this.#persistent = !!persistent;
    }

    mergeArguments(args)
    {
        if (!this.#persistent || !this.#items.hasOwnProperty(args[0]))
        {
            return args;
        }
        const {arguments: persistentArgs} = this.#items[args[0]];
        args.forEach((arg, i) =>
        {
            persistentArgs[i] = arg;
        });
        return persistentArgs;
    }

    add(key, item)
    {
        this.#items[key] = item;
        return this;
    }

    createItem(key, id, args)
    {
        this.add(key, {
            start: Date.now(),
            id,
            arguments: args
        });
        return this;
    }

    remove(key = null)
    {
        if (this.#persistent) { return this; }
        const items = this.#items;
        if (key === null)
        {
            this.keys.forEach((k) => { delete items[k]; });
            return this;
        }
        delete items[key];
        return this;
    }

    iterateOver(handler, key = null)
    {
        const items = this.#items;
        const keys = key ? [key] : this.keys;
        let i = keys.length;
        while (i--)
        {
            const k = keys[i];
            const item = items[k];
            if (item === undefined) { return this; }
            handler(k, item);
        }
        return this;
    }

    cancel(clearHandler, key)
    {
        this.iterateOver(
            (k, item) =>
            {
                this.remove(k);
                if (item.resumeId)
                {
                    clearTimeout(item.resumeId);
                }
                clearHandler(item.id);
            },
            key
        );
        return this;
    }

    copyForResume(targetStash, resumeTimeHandler, key)
    {
        const time = Date.now();
        this.iterateOver(
            (k, item) =>
            {
                item.resumeTime = resumeTimeHandler(item.start, time, item.arguments[2]);
                targetStash.add(k, item);
            },
            key
        );
        return this;
    }
}

/** Dieses Mixin erweitert Objekte oder Klassen um gekapselte und benamte Timeouts, Intervalle und FrameTimer.
 * @mixin Timeable
 */
const Timeable = superClass => class extends superClass
{
    #timerStashes = {
        timeouts: new TimerStash(),
        intervals: new TimerStash(),
        frameTimers: new TimerStash()
    };

    #pausedStashes = {
        timeouts: new TimerStash(),
        intervals: new TimerStash(),
        frameTimers: new TimerStash()
    };

    get _timerStashes()
    {
        throw new Error('Stashes are private. Please use the method "getKeysOf".');
    }

    get _pausedStashes()
    {
        throw new Error('Stashes are private. Please use the method "getPausedKeysOf".');
    }

    /**
     * @memberof Timeable#
     * @param {string} key
     * @param {Function=} callback
     * @param {number} [delay=0]
     * @return {this}
     */
    setTimeout(...args)
    {
        const {timeouts} = this.#timerStashes;
        args = timeouts.mergeArguments(args);
        const [key] = args;
        const callback = args[1];
        const delay = args[2] || 0;

        if (!key || !callback) { return this; }

        this.cancelTimeout(key);
        timeouts.createItem(
            key,
            setTimeout(
                () =>
                {
                    timeouts.remove(key);
                    callback();
                },
                delay
            ),
            args
        );
        return this;
    }

    /** Beendet alle Timeouts oder nur den mit dem übergebenen Key.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @param {boolean} [cancelPaused=true]
     * @return {this}
     */
    cancelTimeout(key = null, cancelPaused = true)
    {
        this.#timerStashes.timeouts.cancel(
            id => clearTimeout(id),
            key
        );
        if (cancelPaused)
        {
            this.#pausedStashes.timeouts.remove(key);
        }
        return this;
    }

    /** Pausiert alle Timeouts oder nur den mit dem übergebenen Key.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @return {this}
     */
    pauseTimeout(key = null)
    {
        this.#timerStashes.timeouts.copyForResume(
            this.#pausedStashes.timeouts,
            (start, time, delay) => delay - (time - start),
            key
        );
        return this.cancelTimeout(key, false);
    }

    /** Setzt alle pausierten Timeouts oder nur den mit dem übergebenen Key fort.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @return {this}
     */
    resumeTimeout(key = null)
    {
        this.#pausedStashes.timeouts.iterateOver(
            (k, item) =>
            {
                item.id = setTimeout(
                    () =>
                    {
                        this.#timerStashes.timeouts.remove(k);
                        item.arguments[1]();
                    },
                    item.resumeTime
                );
                item.start = Date.now() + item.resumeTime - item.arguments[2];
                this.#timerStashes.timeouts.add(k, item);
            },
            key
        ).remove(key);
        return this;
    }

    /**
     * @memberof Timeable#
     * @param {string} key
     * @param {Function=} callback
     * @param {number=} delay
     * @return {this}
     */
    setInterval(...args)
    {
        const {intervals} = this.#timerStashes;
        args = intervals.mergeArguments(args);
        const [key, callback, delay] = args;

        if (!key || !callback) { return this; }

        this.cancelInterval(key);
        intervals.createItem(
            key,
            setInterval(callback, delay),
            args
        );
        return this;
    }

    /** Beendet alle Intervalle oder nur das mit dem übergebenen Key.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @param {boolean} [cancelPaused=true]
     * @return {this}
     */
    cancelInterval(key = null, cancelPaused = true)
    {
        this.#timerStashes.intervals.cancel(
            id => clearInterval(id),
            key
        );
        if (cancelPaused)
        {
            this.#pausedStashes.intervals.remove(key);
        }
        return this;
    }

    /** Pausiert alle Intervalle oder nur das mit dem übergebenen Key.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @return {this}
     */
    pauseInterval(key = null)
    {
        this.#timerStashes.intervals.copyForResume(
            this.#pausedStashes.intervals,
            (start, time, delay) => delay - ((time - start) % delay),
            key
        );
        return this.cancelInterval(key, false);
    }

    /** Setzt alle pausierten Intervalle oder nur das mit dem übergebenen Key fort.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @return {this}
     */
    resumeInterval(key = null)
    {
        this.#pausedStashes.intervals.iterateOver(
            (k, item) =>
            {
                item.resumeId = setTimeout(
                    () =>
                    {
                        item.arguments[1]();
                        this.setInterval(...item.arguments);
                    },
                    item.resumeTime
                );
                item.start = Date.now() + item.resumeTime - item.arguments[2];
                this.#timerStashes.intervals.add(k, item);
            },
            key
        ).remove(key);
        return this;
    }

    /**
     * @memberof Timeable#
     * @param {string} key
     * @param {frameTimer~TimerOptions=} options
     * @return {this}
     */
    setFrameTimer(...args)
    {
        const {frameTimers} = this.#timerStashes;
        args = frameTimers.mergeArguments(args);
        const [key, options] = args;

        if (!key || !options) { return this; }

        this.cancelFrameTimer(key);

        const removeCallback = () =>
        {
            frameTimers.remove(key);
        };

        if (typeof options.removeCallback !== 'function')
        {
            options.removeCallback = removeCallback;
        }
        else
        {
            const originalRemoveCallback = options.removeCallback;
            options.removeCallback = () =>
            {
                removeCallback();
                originalRemoveCallback();
            };
        }

        frameTimers.createItem(
            key,
            frameTimer.add(options),
            args
        );
        return this;
    }

    /** Beendet alle FrameTimer oder nur den mit dem übergebenen Key.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @param {boolean} [cancelPaused=true]
     * @return {this}
     */
    cancelFrameTimer(key = null, cancelPaused = true)
    {
        this.#timerStashes.frameTimers.cancel(
            id => frameTimer.cancel(id),
            key
        );
        if (cancelPaused)
        {
            this.#pausedStashes.frameTimers.remove(key);
        }
        return this;
    }

    /** Pausiert alle FrameTimer oder nur das mit dem übergebenen Key.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @return {this}
     */
    pauseFrameTimer(key = null)
    {
        this.#timerStashes.frameTimers.copyForResume(
            this.#pausedStashes.frameTimers,
            () => 0,
            key
        );
        return this.cancelFrameTimer(key, false);
    }

    /** Setzt alle pausierten FrameTimer oder nur das mit dem übergebenen Key fort.
     * @memberof Timeable#
     * @param {string} [key=null]
     * @return {this}
     */
    resumeFrameTimer(key = null)
    {
        this.#pausedStashes.frameTimers.iterateOver(
            (k, item) =>
            {
                this.setFrameTimer(...item.arguments);
            },
            key
        ).remove(key);
        return this;
    }

    /** Beendet alle Timer.
     * @memberof Timeable#
     * @return {this}
     */
    cancelTimers()
    {
        return this.cancelTimeout().cancelInterval().cancelFrameTimer();
    }

    /** Pausiert alle Timer.
     * @memberof Timeable#
     * @return {this}
     */
    pauseTimers()
    {
        return this.pauseTimeout().pauseInterval().pauseFrameTimer();
    }

    /** Setzt alle Timer fort.
     * @memberof Timeable#
     * @return {this}
     */
    resumeTimers()
    {
        return this.resumeTimeout().resumeInterval().resumeFrameTimer();
    }

    /** Liefert alle Keys eines Timer Stashes zurück.
     * @memberof Timeable#
     * @param {'timeouts'|'intervals'|'frameTimers'} stash
     * @return Array
     */
    getKeysOf(stash)
    {
        return this.#timerStashes[stash].keys;
    }

    /** Liefert alle pausierten Keys eines Timer Stashes zurück.
     * @memberof Timeable#
     * @param {'timeouts'|'intervals'|'frameTimers'} stash
     * @return Array
     */
    getPausedKeysOf(stash)
    {
        return this.#pausedStashes[stash].keys;
    }

    /** Nach Ablauf oder Aufruf der ``cancel``-Methoden werden die Timer-Informationen nicht entfernt und
     * können über die ``set``-Methode wieder aufgerufen werden.
     * @memberof Timeable#
     * @param {boolean} persistent
     * @return {this}
     */
    setTimersPersistent(persistent)
    {
        Object.values(this.#timerStashes).forEach(
            (stash) => { stash.persistent = persistent; }
        );
        return this;
    }
};

Timeable.sorting = 5;

export default Timeable;
